import React, { useEffect, useRef, useState, useContext } from "react";
import AgoraRTC from "agora-rtc-sdk-ng";
import {
    Box,
    Paper,
    Typography,
    Button,
    IconButton,
    Avatar,
    TextField,
    Chip,
    Slide,
    Backdrop,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Snackbar,
} from "@mui/material";
import {
    Mic,
    MicOff,
    Videocam,
    VideocamOff,
    CallEnd,
    VolumeUp,
    Fullscreen,
    Receipt,
    Close,
    ArrowBack,
    CheckCircle,
} from "@mui/icons-material";
import {
    useLocation,
    useNavigate,
    UNSAFE_NavigationContext as NavigationContext,
} from "react-router-dom";
import api from "../../api/api";
import { PictureInPictureAlt } from "@mui/icons-material";
import { OpenInFull } from "@mui/icons-material";
import en_ja from "../../en-ja-map";


const APP_ID = "048b34251f354411a8b6cfe2c5b8b982";

/** Block SPA navigations using the router's underlying history. */
function useNavBlock(when, onBlock) {
    const { navigator } = useContext(NavigationContext);
    useEffect(() => {
        if (!when || !navigator || typeof navigator.block !== "function") return;
        const unblock = navigator.block((tx) => {
            // tx: { action, location, retry }
            onBlock?.(tx);
        });
        return unblock;
    }, [navigator, when, onBlock]);
}

export default function AgoraCall() {
    const localRef = useRef(null);
    const remoteRef = useRef(null);
    const [client] = useState(() =>
        AgoraRTC.createClient({ mode: "rtc", codec: "vp8" })
    );
    const [joined, setJoined] = useState(false);
    const [micEnabled, setMicEnabled] = useState(true);
    const [videoEnabled, setVideoEnabled] = useState(true);
    const [localAudioTrack, setLocalAudioTrack] = useState(null);
    const [localVideoTrack, setLocalVideoTrack] = useState(null);
    const [remoteUsers, setRemoteUsers] = useState({});
    const [isCompactMode, setIsCompactMode] = useState(false);

    // Notes (replacing Prescription) + submit state
    const [notesOpen, setNotesOpen] = useState(false);
    const [notes, setNotes] = useState(" ");
    const notesSubmittedRef = useRef(false);
    const [notesSubmitted, setNotesSubmitted] = useState(false);

    // Track peer + blocked navigation
    const lastSeenRemoteUid = useRef(null);
    const pendingTxRef = useRef(null);

    // Dialogs / notices
    const [showNavigationBlock, setShowNavigationBlock] = useState(false);
    const [showCompletionDialog, setShowCompletionDialog] = useState(false);
    const [showBackgroundNotice, setShowBackgroundNotice] = useState(false);
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);

    const location = useLocation();
    const navigate = useNavigate();

    // Appointment context
    const clinicId = location.state?.clinicId ?? null;
    const channelName = location.state?.channelName ?? null;
    const clinicToken = location.state?.clinicToken ?? null;
    const patientName = location.state?.patientName ?? "Patient";
    const appointmentId = location.state?.appointmentId ?? "Patient";
    const patientId = location.state?.userId ?? null;
    const expectedPatientUid = location.state?.user?.id ?? 2;
    const clinicCheckinData = location.state?.clinic_checkin_data ?? null;
    const rawCheckin = clinicCheckinData;
    const checkinData = typeof rawCheckin === 'string'
        ? (() => { try { return JSON.parse(rawCheckin) } catch { return {} } })()
        : (rawCheckin || {});

    // Block internal SPA navigation while on a call
    useNavBlock(joined, (tx) => {
        // Save the attempted transition (in case you ever want to use tx.retry()).
        pendingTxRef.current = tx;
        // Show your existing "Complete appointment?" dialog instead of the custom nav-block dialog.
        setShowCompletionDialog(true);
    });

    // Warn on close/refresh (native browser dialog)
    useEffect(() => {
        if (!joined) return;
        const handleBeforeUnload = (e) => {
            e.preventDefault();
            e.returnValue = "";
            return "";
        };
        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => window.removeEventListener("beforeunload", handleBeforeUnload);
    }, [joined]);

    // Background notice (just a small chip)
    useEffect(() => {
        const handleVis = () => setShowBackgroundNotice(document.hidden && joined);
        document.addEventListener("visibilitychange", handleVis);
        return () => document.removeEventListener("visibilitychange", handleVis);
    }, [joined]);

    // Agora: event wiring
    useEffect(() => {
        if (!clinicId || !channelName || !clinicToken) {
            console.error("❌ Missing required appointment data");
            alert("Missing appointment information");
            return;
        }

        const handleUserPublished = async (user, mediaType) => {
            try {
                await client.subscribe(user, mediaType);
                if (mediaType === "video" && user.videoTrack && remoteRef.current) {
                    user.videoTrack.play(remoteRef.current);
                }
                if (mediaType === "audio" && user.audioTrack) {
                    user.audioTrack.play();
                }
                setRemoteUsers((prev) => ({ ...prev, [user.uid]: user }));
                lastSeenRemoteUid.current = user.uid;
            } catch (err) {
                console.error("subscribe fail", mediaType, user.uid, err);
            }
        };

        const handleUserUnpublished = (user) => {
            setRemoteUsers((prev) => {
                const updated = { ...prev };
                if (updated[user.uid]) updated[user.uid] = user;
                return updated;
            });
            lastSeenRemoteUid.current = user.uid;
        };

        const handleUserJoined = async (user) => {
            lastSeenRemoteUid.current = user.uid;
            try {
                if (user.hasVideo) await client.subscribe(user, "video");
                if (user.hasAudio) await client.subscribe(user, "audio");
            } catch (err) {
                console.error("subscribe existing tracks fail", user.uid, err);
            }
        };

        const handleUserLeft = (user) => {
            setRemoteUsers((prev) => {
                const updated = { ...prev };
                delete updated[user.uid];
                return updated;
            });
        };

        client.on("user-published", handleUserPublished);
        client.on("user-unpublished", handleUserUnpublished);
        client.on("user-joined", handleUserJoined);
        client.on("user-left", handleUserLeft);

        return () => {
            client.off("user-published", handleUserPublished);
            client.off("user-unpublished", handleUserUnpublished);
            client.off("user-joined", handleUserJoined);
            client.off("user-left", handleUserLeft);
        };
    }, [client, clinicId, channelName, clinicToken, expectedPatientUid]);

    // Submit notes (stub)
    const submitNotes = async (payload) => {
        console.log("Submitting notes", payload);
        notesSubmittedRef.current = true;
        setNotesSubmitted(true);
        return notes;
    };

    // End call flow: autosubmit notes → leave → show completion dialog
    const endCallFlow = async (reason) => {
        console.log("🔚 End call flow:", reason);
        if (joined) {
            const notesToSubmit = notes.trim().length > 0 ? notes.trim() : "No notes added during session.";
            if (notes.trim() && !notesSubmittedRef.current) {
                const peerUid =
                    Object.keys(remoteUsers)[0] ?? lastSeenRemoteUid.current ?? null;
                const notesPayload = {
                    clinicId,
                    channelName,
                    peerUid,
                    text: notesToSubmit,
                    createdAt: Date.now(),
                };
                await submitNotes(notesPayload);
            }
            await leaveChannel();
        }
        setShowCompletionDialog(true);
    };

    const joinChannel = async () => {
        try {
            await client.join(APP_ID, channelName, clinicToken, clinicId);
            const [audioTrack, videoTrack] =
                await AgoraRTC.createMicrophoneAndCameraTracks(
                    { ANC: true, AGC: true, volume: 100 },
                    { encoderConfig: "480p_1", optimizationMode: "motion" }
                );
            setLocalAudioTrack(audioTrack);
            setLocalVideoTrack(videoTrack);
            if (localRef.current) videoTrack.play(localRef.current);
            await client.publish([audioTrack, videoTrack]);

            const existingRemoteUsers = client.remoteUsers;
            for (const user of existingRemoteUsers) {
                try {
                    if (user.hasVideo && !user.videoTrack) await client.subscribe(user, "video");
                    if (user.hasAudio && !user.audioTrack) await client.subscribe(user, "audio");
                } catch (err) {
                    console.error("subscribe existing user fail", user.uid, err);
                }
            }
            setJoined(true);
        } catch (err) {
            console.error("join fail", err);
            let msg = "Failed to join channel";
            if (err.message?.includes("INVALID_TOKEN")) msg = "Invalid token";
            else if (err.message?.includes("CHANNEL_REJECTED")) msg = "Channel rejected";
            else if (err.message?.includes("TIMEOUT")) msg = "Connection timeout";
            alert(`${msg}: ${err.message}`);
        }
    };

    const leaveChannel = async () => {
        try {
            if (localAudioTrack) {
                localAudioTrack.stop();
                localAudioTrack.close();
            }
            if (localVideoTrack) {
                localVideoTrack.stop();
                localVideoTrack.close();
            }
            await client.leave();
            setJoined(false);
            setLocalAudioTrack(null);
            setLocalVideoTrack(null);
            setRemoteUsers({});
        } catch (err) {
            console.error("leave fail", err);
        }
    };

    const toggleMic = async () => {
        if (!localAudioTrack) return;
        await localAudioTrack.setEnabled(!micEnabled);
        setMicEnabled((v) => !v);
    };

    const toggleVideo = async () => {
        if (!localVideoTrack) return;
        await localVideoTrack.setEnabled(!videoEnabled);
        setVideoEnabled((v) => !v);
    };

    // Navigation block dialog handlers
    const handleEndCallFromBlock = async () => {
        setShowNavigationBlock(false);
        await endCallFlow("navigation");
        // We do NOT continue the originally intended navigation.
        pendingTxRef.current = null;
    };

    const handleStayOnPage = () => {
        setShowNavigationBlock(false);
        pendingTxRef.current = null; // discard blocked transition
    };

    // Completion dialog handlers
    const handleCompletionYes = async () => {
        // Ensure notes are submitted (if any) and the channel is left cleanly
        if (joined) {
            const peerUidLocal =
                Object.keys(remoteUsers)[0] ?? lastSeenRemoteUid.current ?? null;
            if (notes.trim() && !notesSubmittedRef.current) {
                await submitNotes({
                    clinicId,
                    channelName,
                    peerUid: peerUidLocal,
                    text: notes,
                    createdAt: Date.now(),
                });
            }
            await leaveChannel();
        }

        const peerUid = Object.keys(remoteUsers)[0] ?? lastSeenRemoteUid.current ?? null;

        const completeObj = {
            appointmentId: appointmentId,
            status: "completed",
            clinicId: clinicId,
            userId: patientId
        }

        api.post("v1/clinic/appointment/complete", completeObj)
            .then((res) => {
                console.log("Appointment marked as completed", res.data);
            })

        const notesObj = {
            appointment_id: appointmentId,
            notes: notes,
        }

        api.post("/v1/clinic/appointment/patient-notes", notesObj)
            .then((res) => {
                console.log("Notes saved", res.data);
            })

        // Close completion dialog and show success dialog
        setShowCompletionDialog(false);
        setShowSuccessDialog(true);
        pendingTxRef.current = null;
    };

    const handleCompletionNo = () => {
        setShowCompletionDialog(false);
        pendingTxRef.current = null;
    };

    // Success dialog handler
    const handleGoToBookingManagement = () => {
        setShowSuccessDialog(false);
        navigate("/booking-management");
    };

    // Manual notes submit
    const handleSubmitNotes = async () => {
        if (!notes.trim()) return;
        const peerUid =
            Object.keys(remoteUsers)[0] ?? lastSeenRemoteUid.current ?? null;

        const notesObj = {
            appointment_id: appointmentId,
            notes: notes,
        }

        api.post("/v1/clinic/appointment/patient-notes", notesObj)
            .then((res) => {
                console.log("Notes saved", res.data);
            })
    };

    const handleBackClick = () => {
        if (joined) {
            // If in a call, show the same completion dialog
            setShowCompletionDialog(true);
        } else {
            // If not in a call, navigate directly
            console.log("Navigating back to booking management");
            navigate("/booking-management");
        }
    };

    return (
        <Box
            sx={{
                p: 2,
                backgroundColor: "#fafbfc",
                minHeight: "100vh",
                overflow: "hidden",
                position: "relative",
            }}
        >
            {/* Background call notice (offset to avoid top-left chip overlap) */}
            <Snackbar
                open={showBackgroundNotice}
                anchorOrigin={{ vertical: "top", horizontal: "left" }}
                sx={{ zIndex: 2000, mt: "56px", ml: "8px" }}
            >
                <Chip
                    label={en_ja["Call running in background"]}
                    sx={{
                        backgroundColor: "#28a745",
                        color: "white",
                        fontWeight: "600",
                        fontSize: "0.75rem",
                    }}
                />
            </Snackbar>

            <Box sx={{
                position: 'absolute',
                top: { xs: 16, md: 24 },
                left: { xs: 16, md: 24 },
                zIndex: 1000
            }}>
                <IconButton
                    onClick={handleBackClick}
                    sx={{
                        backgroundColor: "rgba(255,255,255,0.9)",
                        color: "#6c757d",
                        width: { xs: 40, md: 44 },
                        height: { xs: 40, md: 44 },
                        backdropFilter: "blur(8px)",
                        border: "1px solid rgba(255,255,255,0.2)",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                        "&:hover": {
                            backgroundColor: "white",
                            color: "#dc3545",
                            transform: "scale(1.05)"
                        },
                    }}
                >
                    <ArrowBack fontSize="small" />
                </IconButton>
            </Box>

            {/* Main container with responsive padding */}
            <Box sx={{
                display: 'flex',
                height: '100vh',
                backgroundColor: "#fafbfc",
                p: { xs: 1, sm: 2, md: 3 }, // Responsive padding
                gap: { xs: 1, sm: 2, md: 3 } // Responsive gap
            }}>

                {/* Video Area - 70% */}
                <Box sx={{
                    flex: '0 0 70%',
                    position: 'relative',
                    background: '#000',
                    borderRadius: '16px',
                    overflow: 'hidden',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.15)'
                }}>
                    {/* Remote video */}
                    <Box
                        ref={remoteRef}
                        sx={{
                            width: "100%",
                            height: "100%",
                            background: "#000",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        {!joined && (
                            <Typography variant="h6" color="white">
                                {en_ja["Click Join to start video call"] || "Click Join to start video call"}
                            </Typography>
                        )}
                        {joined && Object.keys(remoteUsers).length === 0 && (
                            <Typography variant="h6" color="white">
                                {en_ja["Waiting for patient to join..."] || "Waiting for patient to join..."}
                            </Typography>
                        )}
                    </Box>

                    {/* Local video */}
                    <Box
                        ref={localRef}
                        sx={{
                            position: "absolute",
                            bottom: { xs: 15, md: 25 }, // Responsive positioning
                            right: { xs: 15, md: 25 },
                            width: { xs: 160, md: 200 }, // Responsive sizing
                            height: { xs: 120, md: 150 },
                            borderRadius: "12px",
                            overflow: "hidden",
                            border: "3px solid white",
                            //boxShadow: "0 4px 16px rgba(0,0,0,0.3)",
                            //background: "#000",
                        }}
                    />

                    {/* Status + Controls overlay */}
                    <Box sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
                        p: { xs: 2, md: 3 } // More padding
                    }}>
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 2 }}>
                            <Chip
                                label={joined ? en_ja["Connected"] || "Connected" : en_ja["Disconnected"] || "Disconnected"}
                                size="small"
                                sx={{
                                    backgroundColor: joined ? "#28a745" : "#dc3545",
                                    color: "white",
                                    fontWeight: "600",
                                }}
                            />
                        </Box>

                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            gap: { xs: 1.5, md: 2 }, // More spacing between controls
                            flexWrap: 'wrap' // Wrap on smaller screens
                        }}>
                            <IconButton
                                sx={{
                                    backgroundColor: micEnabled ? "#28a745" : "#dc3545",
                                    color: "white",
                                    width: { xs: 44, md: 48 }, // Slightly larger buttons
                                    height: { xs: 44, md: 48 },
                                    "&:hover": { backgroundColor: micEnabled ? "#218838" : "#c82333" },
                                }}
                                onClick={toggleMic}
                                disabled={!joined}
                            >
                                {micEnabled ? <Mic /> : <MicOff />}
                            </IconButton>

                            <IconButton
                                sx={{
                                    backgroundColor: videoEnabled ? "#28a745" : "#dc3545",
                                    color: "white",
                                    width: { xs: 44, md: 48 },
                                    height: { xs: 44, md: 48 },
                                    "&:hover": { backgroundColor: videoEnabled ? "#218838" : "#c82333" },
                                }}
                                onClick={toggleVideo}
                                disabled={!joined}
                            >
                                {videoEnabled ? <Videocam /> : <VideocamOff />}
                            </IconButton>

                            {/* <IconButton
                                sx={{
                                    backgroundColor: "#6c757d",
                                    color: "white",
                                    width: { xs: 44, md: 48 },
                                    height: { xs: 44, md: 48 }
                                }}
                            >
                                <VolumeUp />
                            </IconButton>

                            <IconButton
                                sx={{
                                    backgroundColor: "#6c757d",
                                    color: "white",
                                    width: { xs: 44, md: 48 },
                                    height: { xs: 44, md: 48 }
                                }}
                            >
                                <Fullscreen />
                            </IconButton> */}

                            {!joined ? (
                                <Button
                                    variant="contained"
                                    onClick={joinChannel}
                                    sx={{
                                        backgroundColor: "#28a745",
                                        px: { xs: 2, md: 3 }, // More padding
                                        py: { xs: 1, md: 1.5 },
                                        borderRadius: "24px",
                                        fontSize: { xs: "0.8rem", md: "0.9rem" },
                                        "&:hover": { backgroundColor: "#218838" },
                                        minWidth: { xs: 100, md: 120 }
                                    }}
                                >
                                    {en_ja["Join Call"]}
                                </Button>
                            ) : (
                                <IconButton
                                    sx={{
                                        backgroundColor: "#dc3545",
                                        color: "white",
                                        width: { xs: 44, md: 48 },
                                        height: { xs: 44, md: 48 },
                                        "&:hover": { backgroundColor: "#c82333" },
                                    }}
                                    onClick={() => endCallFlow("hangup")}
                                >
                                    <CallEnd />
                                </IconButton>
                            )}
                        </Box>
                    </Box>
                </Box>

                {/* Medical Panel - 30% */}
                <Box sx={{
                    flex: '0 0 30%',
                    background: 'white',
                    borderRadius: '16px',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    minWidth: { xs: 320, md: 380 } // Ensure minimum width
                }}>

                    {/* Patient Header */}
                    <Box sx={{
                        p: { xs: 2, md: 3 },
                        borderBottom: '1px solid rgba(0,0,0,0.06)',
                        background: 'linear-gradient(135deg, #ffffff 0%, #fafbfc 100%)'
                    }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <Avatar sx={{
                                width: { xs: 40, md: 44 },
                                height: { xs: 40, md: 44 },
                                backgroundColor: "#dc3545"
                            }}>
                                👨‍⚕️
                            </Avatar>
                            <Box>
                                <Typography variant="subtitle1" fontWeight="600" sx={{ fontSize: { xs: '1rem', md: '1.1rem' } }}>
                                    {checkinData?.full_name || patientName}
                                </Typography>
                                <Typography variant="caption" color="#6c757d">
                                    {checkinData?.age && ` • Age: ${checkinData.age}`}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    {/* Health Metrics */}
                    <Box sx={{
                        flex: '0 0 40%',
                        p: { xs: 2, md: 3 },
                        borderBottom: '1px solid rgba(0,0,0,0.06)',
                        overflow: 'auto'
                    }}>
                        <Typography variant="subtitle2" fontWeight="600" gutterBottom sx={{ mb: 2 }}>
                            {en_ja['Vital Signs']}
                        </Typography>

                        {/* Check if vital signs data exists */}
                        {(checkinData?.height || checkinData?.weight || checkinData?.bmi) ? (
                            <>
                                <Box sx={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr',
                                    gap: { xs: 1.5, md: 2 }
                                }}>
                                    {/* Height */}
                                    {checkinData?.height && (
                                        <Box sx={{
                                            p: { xs: 1.5, md: 2 },
                                            background: '#f8f9fa',
                                            borderRadius: '10px',
                                            border: '1px solid rgba(0,0,0,0.04)'
                                        }}>
                                            <Typography variant="caption" color="#6c757d">{en_ja["Height"]}</Typography>
                                            <Typography variant="h6" color="#007bff" sx={{ fontSize: { xs: '1.1rem', md: '1.25rem' } }}>
                                                {checkinData.height} cm
                                            </Typography>
                                        </Box>
                                    )}

                                    {/* Weight */}
                                    {checkinData?.weight && (
                                        <Box sx={{
                                            p: { xs: 1.5, md: 2 },
                                            background: '#f8f9fa',
                                            borderRadius: '10px',
                                            border: '1px solid rgba(0,0,0,0.04)'
                                        }}>
                                            <Typography variant="caption" color="#6c757d">{en_ja["Weight"]}</Typography>
                                            <Typography variant="h6" color="#28a745" sx={{ fontSize: { xs: '1.1rem', md: '1.25rem' } }}>
                                                {checkinData.weight} kg
                                            </Typography>
                                        </Box>
                                    )}

                                    {/* BMI */}
                                    {checkinData?.bmi && (
                                        <Box sx={{
                                            p: { xs: 1.5, md: 2 },
                                            background: '#f8f9fa',
                                            borderRadius: '10px',
                                            border: '1px solid rgba(0,0,0,0.04)'
                                        }}>
                                            <Typography variant="caption" color="#6c757d">{en_ja["BMI"]}</Typography>
                                            <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', md: '1.25rem' } }}>
                                                {checkinData.bmi}
                                            </Typography>
                                        </Box>
                                    )}

                                    {/* Blood Pressure - if available */}
                                    {checkinData?.blood_pressure && (
                                        <Box sx={{
                                            p: { xs: 1.5, md: 2 },
                                            background: '#f8f9fa',
                                            borderRadius: '10px',
                                            border: '1px solid rgba(0,0,0,0.04)'
                                        }}>
                                            <Typography variant="caption" color="#6c757d">{en_ja["Blood Pressure"]}</Typography>
                                            <Typography variant="h6" color="#dc3545" sx={{ fontSize: { xs: '1.1rem', md: '1.25rem' } }}>
                                                {checkinData.blood_pressure}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>

                                {/* Additional patient info */}
                                <Box sx={{ mt: { xs: 2, md: 3 } }}>
                                    {checkinData?.sex && (
                                        <Box sx={{ mb: 1.5 }}>
                                            <Typography variant="caption" color="#6c757d" sx={{ fontSize: '0.7rem' }}>
                                                {en_ja["Gender"]}
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                {checkinData.sex}
                                            </Typography>
                                        </Box>
                                    )}

                                    {checkinData?.symptoms && (
                                        <Box>
                                            <Typography variant="caption" color="#6c757d" gutterBottom sx={{ display: 'block', mb: 1, fontSize: '0.7rem' }}>
                                                {en_ja["Reported Symptoms"]}
                                            </Typography>
                                            <Box sx={{
                                                p: 1.5,
                                                background: '#fff3cd',
                                                borderRadius: '8px',
                                                border: '1px solid #ffeaa7'
                                            }}>
                                                <Typography variant="body2" color="#856404" sx={{ fontSize: '0.8rem' }}>
                                                    {checkinData.symptoms}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                            </>
                        ) : (
                            /* Show message when no vital data is available */
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '120px',
                                background: '#f8f9fa',
                                borderRadius: '12px',
                                border: '2px dashed #dee2e6',
                                textAlign: 'center'
                            }}>
                                <Typography variant="body2" color="#6c757d" sx={{ mb: 1 }}>
                                    {en_ja["Health Records Not Available"]}
                                </Typography>
                                <Typography variant="caption" color="#9ca3af">
                                    {en_ja["Vital signs have not been collected or updated for this appointment"]}
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    {/* Notes Section */}
                    <Box sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        p: { xs: 2, md: 3 } // More padding
                    }}>
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: { xs: 2, md: 3 } // More margin
                        }}>
                            <Typography variant="subtitle2" fontWeight="600">
                                {en_ja["Session Notes"]}
                            </Typography>
                        </Box>

                        <TextField
                            fullWidth
                            multiline
                            placeholder={en_ja["Type your observations, diagnosis, treatment plan..."]}
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            sx={{
                                flex: 1,
                                "& .MuiOutlinedInput-root": {
                                    height: '100%',
                                    alignItems: 'flex-start',
                                    borderRadius: "10px",
                                    "&:hover fieldset": { borderColor: "#dc3545" },
                                    "&.Mui-focused fieldset": { borderColor: "#dc3545" },
                                },
                                "& .MuiInputBase-input": {
                                    height: '100% !important',
                                    overflow: 'auto !important',
                                    fontSize: { xs: '0.9rem', md: '1rem' }
                                }
                            }}
                        />
                        <Typography variant="caption" color="#6c757d">
                            {en_ja["When call ends notes will be saved automatically"]}
                        </Typography>

                        {/* <Button
                            variant="contained"
                            fullWidth
                            onClick={handleSubmitNotes}
                            disabled={!notes.trim() || notesSubmitted}
                            sx={{
                                mt: { xs: 2, md: 3 }, // More margin
                                py: { xs: 1.5, md: 2 }, // More padding
                                backgroundColor: "#dc3545",
                                borderRadius: "10px",
                                fontSize: { xs: '0.9rem', md: '1rem' },
                                fontWeight: "600",
                                "&:hover": { backgroundColor: "#c82333" },
                            }}
                        >
                            {notesSubmitted ? "Notes Saved" : "Save Notes"}
                        </Button> */}
                    </Box>
                </Box>
            </Box>

            {/* Navigation block dialog */}
            <Dialog
                open={showNavigationBlock}
                onClose={handleStayOnPage}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: "16px", boxShadow: "0 8px 32px rgba(0,0,0,0.2)" },
                }}
            >
                <DialogTitle sx={{ textAlign: "center", pb: 1 }}>
                    {en_ja["You are in a call. End the call before leaving this page."]}
                </DialogTitle>
                <DialogActions sx={{ justifyContent: "center", gap: 1, pb: 2 }}>
                    <Button
                        onClick={handleEndCallFromBlock}
                        variant="contained"
                        sx={{
                            backgroundColor: "#dc3545",
                            color: "white",
                            borderRadius: "8px",
                            "&:hover": { backgroundColor: "#c82333" },
                        }}
                    >
                        {en_ja["End Call"]}
                    </Button>
                    <Button
                        onClick={handleStayOnPage}
                        variant="outlined"
                        sx={{
                            borderColor: "#6c757d",
                            color: "#6c757d",
                            borderRadius: "8px",
                            "&:hover": {
                                borderColor: "#5a6268",
                                backgroundColor: "rgba(108,117,125,0.04)",
                            },
                        }}
                    >
                        {en_ja["Stay on Page"]}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Completion dialog */}
            <Dialog
                open={showCompletionDialog}
                onClose={() => { }}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: "16px", boxShadow: "0 8px 32px rgba(0,0,0,0.2)" },
                }}
            >
                <DialogTitle sx={{ textAlign: "center", pb: 1 }}>
                    {joined ? en_ja["Complete appointment?"] || "Complete appointment?" : en_ja["Leave consultation?"] || "Leave consultation?"}
                </DialogTitle>
                <DialogContent sx={{ textAlign: "center", pb: 2 }}>
                    <Typography color="#6c757d">
                        {joined
                            ? en_ja["Was the appointment successfully completed?"] || "Was the appointment successfully completed?"
                            : en_ja["Are you sure you want to go back to booking management?"] || "Are you sure you want to go back to booking management?"
                        }
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ justifyContent: "center", gap: 1, pb: 2 }}>
                    <Button
                        onClick={handleCompletionYes}
                        variant="contained"
                        sx={{
                            backgroundColor: "#28a745",
                            color: "white",
                            borderRadius: "8px",
                            "&:hover": { backgroundColor: "#218838" },
                        }}
                    >
                        {joined ? en_ja["Yes, Completed"] || "Yes, Completed" : en_ja["Yes, Go Back"] || "Yes, Go Back"}
                    </Button>
                    <Button
                        onClick={handleCompletionNo}
                        variant="outlined"
                        sx={{
                            borderColor: "#6c757d",
                            color: "#6c757d",
                            borderRadius: "8px",
                            "&:hover": {
                                borderColor: "#5a6268",
                                backgroundColor: "rgba(108,117,125,0.04)",
                            },
                        }}
                    >
                        {joined ? en_ja["No"] : en_ja["Stay"]}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Success Dialog */}
            <Dialog
                open={showSuccessDialog}
                onClose={() => { }}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: { 
                        borderRadius: "20px", 
                        boxShadow: "0 12px 40px rgba(0,0,0,0.15)",
                        overflow: "hidden"
                    },
                }}
            >
                <Box sx={{
                    background: "linear-gradient(135deg, #28a745 0%, #20c997 100%)",
                    p: 4,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    position: "relative"
                }}>
                    {/* Success Icon with Animation */}
                    <Box sx={{
                        width: 80,
                        height: 80,
                        borderRadius: "50%",
                        backgroundColor: "rgba(255,255,255,0.2)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 2,
                        animation: "pulse 2s ease-in-out infinite",
                        "@keyframes pulse": {
                            "0%, 100%": { transform: "scale(1)", opacity: 1 },
                            "50%": { transform: "scale(1.05)", opacity: 0.8 }
                        }
                    }}>
                        <CheckCircle sx={{ 
                            fontSize: 56, 
                            color: "white",
                        }} />
                    </Box>

                    <Typography 
                        variant="h5" 
                        fontWeight="700" 
                        color="white" 
                        sx={{ 
                            mb: 1,
                            textAlign: "center",
                            textShadow: "0 2px 4px rgba(0,0,0,0.1)"
                        }}
                    >
                        {en_ja["Treatment Completed"] || "Treatment Completed"}
                    </Typography>

                    <Typography 
                        variant="body2" 
                        color="white" 
                        sx={{ 
                            textAlign: "center",
                            opacity: 0.95,
                            maxWidth: 400
                        }}
                    >
                        {en_ja["Appointment has been successfully completed"] || "Appointment has been successfully completed"}
                    </Typography>
                </Box>

                <DialogContent sx={{ 
                    p: 4, 
                    textAlign: "center",
                    backgroundColor: "#fafbfc"
                }}>
                    <Box sx={{
                        backgroundColor: "white",
                        borderRadius: "12px",
                        p: 3,
                        mb: 3,
                        border: "1px solid #e9ecef",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.04)"
                    }}>
                        <Typography 
                            variant="body1" 
                            color="#1a1a1a" 
                            sx={{ 
                                mb: 2,
                                fontWeight: 500,
                                lineHeight: 1.6
                            }}
                        >
                            {en_ja["Now you can go to Booking Management screen to create prescription"] || 
                             "Now you can go to Booking Management screen to create prescription"}
                        </Typography>

                        <Box sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 1.5,
                            mt: 2,
                            p: 2,
                            backgroundColor: "#f8f9fa",
                            borderRadius: "8px"
                        }}>
                            <Box sx={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                backgroundColor: "#28a745"
                            }} />
                            <Typography variant="body2" color="#6c757d" fontWeight="500">
                                {en_ja["Session notes have been saved"] || "Session notes have been saved"}
                            </Typography>
                        </Box>
                    </Box>

                    <Button
                        variant="contained"
                        fullWidth
                        onClick={handleGoToBookingManagement}
                        sx={{
                            backgroundColor: "#dc3545",
                            color: "white",
                            py: 1.8,
                            borderRadius: "12px",
                            fontSize: "1rem",
                            fontWeight: "600",
                            textTransform: "none",
                            boxShadow: "0 4px 12px rgba(220, 53, 69, 0.3)",
                            "&:hover": { 
                                backgroundColor: "#c82333",
                                boxShadow: "0 6px 16px rgba(220, 53, 69, 0.4)",
                                transform: "translateY(-1px)"
                            },
                            transition: "all 0.2s ease"
                        }}
                    >
                        {en_ja["Go to Booking Management"] || "Go to Booking Management"}
                    </Button>
                </DialogContent>
            </Dialog>

            {/* Notes slide-over */}
            <Backdrop
                open={notesOpen}
                onClick={() => setNotesOpen(false)}
                sx={{ zIndex: 1200, backgroundColor: "rgba(0,0,0,0.3)" }}
            />
            <Slide direction="left" in={notesOpen} mountOnEnter unmountOnExit>
                <Paper
                    sx={{
                        position: "fixed",
                        top: 0,
                        right: 0,
                        width: 550,
                        height: "100vh",
                        zIndex: 1300,
                        background: "white",
                        borderRadius: "24px 0 0 24px",
                        boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                    }}
                >
                    <Box
                        sx={{
                            p: 3,
                            background: "linear-gradient(135deg, #ffffff 0%, #fafbfc 100%)",
                            borderBottom: "1px solid rgba(0,0,0,0.06)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                        }}
                    >
                        <Typography variant="h6" fontWeight="700" color="#1a1a1a" sx={{ fontSize: "1.2rem" }}>
                            {en_ja['Notes']}
                        </Typography>
                        <IconButton
                            onClick={() => setNotesOpen(false)}
                            sx={{
                                backgroundColor: "rgba(220,53,69,0.1)",
                                color: "#dc3545",
                                width: 32,
                                height: 32,
                                "&:hover": { backgroundColor: "rgba(220,53,69,0.2)" },
                            }}
                        >
                            <Close />
                        </IconButton>
                    </Box>

                    <Box
                        sx={{
                            flex: 1,
                            p: 3,
                            overflowY: "auto",
                            "&::-webkit-scrollbar": { width: "6px" },
                            "&::-webkit-scrollbar-track": { backgroundColor: "#f1f3f4" },
                            "&::-webkit-scrollbar-thumb": { backgroundColor: "#c1c8cd", borderRadius: "3px" },
                        }}
                    >
                        <TextField
                            fullWidth
                            multiline
                            rows={12}
                            label="Notes"
                            helperText="These notes will be auto-submitted when you end the call."
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "12px",
                                    "&:hover fieldset": { borderColor: "#dc3545" },
                                    "&.Mui-focused fieldset": { borderColor: "#dc3545" },
                                },
                            }}
                        />
                    </Box>

                    <Box
                        sx={{
                            p: 3,
                            borderTop: "1px solid #e9ecef",
                            background: "linear-gradient(135deg, #ffffff 0%, #fafbfc 100%)",
                        }}
                    >
                        {/* <Button
                            variant="contained"
                            fullWidth
                            onClick={handleSubmitNotes}
                            disabled={!notes.trim() || notesSubmitted}
                            sx={{
                                backgroundColor: "#dc3545",
                                color: "white",
                                py: 1.5,
                                borderRadius: "12px",
                                fontWeight: "600",
                                textTransform: "none",
                                "&:hover": { backgroundColor: "c82333" },
                            }}
                        >
                            {notesSubmitted ? "Notes Submitted" : "Submit Notes"}
                        </Button> */}
                    </Box>
                </Paper>
            </Slide>
        </Box>
    );
}